import React, { useCallback, useState } from "react";

/**
 * useToggleableSeries
 *
 * Tracks which series in a multi-series chart are hidden. Series start visible.
 * Returns:
 *   - isHidden(key) – boolean
 *   - toggle(key)   – flips hidden state for that key
 *   - reset()       – clears all hidden series (everything visible again)
 *   - hidden        – Set<string> for read-only inspection
 */
export function useToggleableSeries() {
  const [hidden, setHidden] = useState(() => new Set());

  const toggle = useCallback((key) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const reset = useCallback(() => setHidden(new Set()), []);
  const isHidden = useCallback((key) => hidden.has(key), [hidden]);

  return { isHidden, toggle, reset, hidden };
}

/**
 * ChartLegend
 *
 * Renders a clickable legend strip for a multi-series chart. Each chip can be
 * clicked to toggle that series on/off. Disabled chips are dimmed and struck
 * through.
 *
 * Props:
 *   keys     – string[] of series IDs (data keys)
 *   colors   – string[] colour palette
 *   labels   – optional dict {key: prettyLabel}
 *   hidden   – Set<string> from useToggleableSeries
 *   onToggle – (key) => void
 *   testIdPrefix – string used to build data-testid for each chip
 */
export default function ChartLegend({
  keys, colors, labels = {}, hidden, onToggle, testIdPrefix = "legend",
}) {
  return (
    <div
      className="flex flex-wrap gap-1.5 mb-3 select-none"
      data-testid={`${testIdPrefix}-strip`}
    >
      {keys.map((k, i) => {
        const off = hidden?.has(k);
        const color = colors[i % colors.length];
        return (
          <button
            key={k}
            type="button"
            onClick={() => onToggle(k)}
            data-testid={`${testIdPrefix}-toggle-${k}`}
            aria-pressed={!off}
            title={off ? "Click to show" : "Click to hide"}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[11px]
                        font-mono uppercase tracking-wider border transition-all
                        ${off
                          ? "border-zinc-200 bg-zinc-50 text-zinc-400 line-through"
                          : "border-zinc-300 bg-white text-zinc-800 hover:border-zinc-400"}`}
          >
            <span
              className="inline-block h-2 w-2 rounded-sm"
              style={{
                backgroundColor: off ? "#D4D4D8" : color,
                opacity: off ? 0.5 : 1,
              }}
            />
            <span>{labels[k] || k}</span>
          </button>
        );
      })}
      {keys.length > 1 && (
        <button
          type="button"
          onClick={() => keys.forEach((k) => hidden.has(k) && onToggle(k))}
          className="text-[10px] uppercase tracking-wider text-zinc-400 hover:text-[#002FA7] transition-colors ml-1"
          data-testid={`${testIdPrefix}-show-all`}
          title="Show all series"
        >
          show all
        </button>
      )}
    </div>
  );
}
